import type {ProductActivityView} from '@shared/productActivity';
import type {SuggestionSelection} from '@shared/canvasSuggestion';
import type {CanvasFollowUpOrigin} from '@shared/canvasFollowUp';
import React from 'react';

export type ChatSuggestion = {
  label: string;
  description?: string;
  value: string;
  displayContent?: string;
  followUpContext?: CanvasFollowUpOrigin;
  productId?: number;
  forcedTool?: { name: string; arguments: Record<string, unknown> };
};

export function ChatSuggestionChoices({ question, pills, onSelect, selection, showQuestion = true, isTyping = false, productActivity, activityUnavailable=false }: {
  productActivity?: ProductActivityView[];
  activityUnavailable?:boolean;
  selection?: SuggestionSelection;
  isTyping?: boolean;
  showQuestion?: boolean;
  question: string;
  pills: ChatSuggestion[];
  onSelect: (pill: ChatSuggestion) => void;
}) {
  if (selection) return <div className="mt-3 space-y-2" data-testid="resolved-suggestion">
    {showQuestion && <p className="text-xs text-muted-foreground">{question}</p>}
    <div><span className="inline-flex max-w-full rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-bold text-foreground">{selection.label}</span></div>
  </div>;
  if (isTyping) return null;
  const activityLabel=(pill:ChatSuggestion)=>{
    const id=Number(pill.productId??pill.forcedTool?.arguments.productId);
    if(!Number.isSafeInteger(id)||id<=0)return null;
    if(activityUnavailable)return 'Product status unavailable — retry';
    if(!productActivity)return 'Checking product…';
    const current=productActivity.find(product=>product.productId===id);
    return current?(current.label||current.unavailableReason||null):'Product unavailable';
  };
  const productChoice = /\bproduct\b/i.test(question) || pills.some(pill => pill.forcedTool?.arguments.productId != null);
  return <div className="mt-3 space-y-2">
    {showQuestion && <p className="text-xs text-muted-foreground">{question}</p>}
    {productChoice && pills.length > 4 ? <div className="relative w-full min-w-0"><select
      aria-label={question}
      value=""
      className="block w-full min-w-0 appearance-none truncate rounded-xl border border-border bg-background py-2 pl-3 pr-10 text-sm leading-5"
      onChange={event => { const pill = pills[Number(event.target.value)]; if (event.target.value !== '' && pill && !activityLabel(pill)) onSelect(pill); }}
    >
      <option value="" disabled>Select a product…</option>
      {pills.map((pill, index) => <option key={index} value={index} disabled={!!activityLabel(pill)}>{pill.label}{activityLabel(pill)?` — ${activityLabel(pill)}`:''}</option>)}
    </select><svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"><path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></div> : <div className="flex flex-wrap gap-2">
      {pills.map((pill, index) => <button
        key={index}
        data-testid={`pill-suggestion-${pill.value}`}
        disabled={!!activityLabel(pill)}
        onClick={() => {if(!activityLabel(pill))onSelect(pill);}}
        className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title={pill.description}
      >{pill.label}{activityLabel(pill)&&<span className="ml-1.5 font-normal">— {activityLabel(pill)}</span>}</button>)}
    </div>}
  </div>;
}
